<template>
    <div class="groups-tab">
        <div class="panel-header">
            <div class="left">
                <strong>그룹 목록</strong>
                <p class="total">총 <span>{{ state.groupList.length }}</span>개의 그룹이 있습니다.</p>
            </div>
            <div class="right create-group">
                <div class="search-form">
                    <input v-model.trim="state.newGroupName" type="text" placeholder="새 그룹명" class="inp-search" @keyup.enter="createGroup" />
                    <button class="btn-create-group" @click="createGroup" :disabled="!state.newGroupName || !selectedCrewId">그룹 생성</button>
                </div>
            </div>
        </div>

        <div class="group-split">
            <aside class="group-list">
                <ul>
                    <li v-for="g in state.groupList" :key="g[GROUP_KEYS.id]"
                        :class="{ selected: g[GROUP_KEYS.id] === state.selectedGroupId }"
                        @click="selectGroup(g[GROUP_KEYS.id])">
                        <span class="name">{{ g[GROUP_KEYS.name] }}</span>
                        <span v-if="g[GROUP_KEYS.id] === state.selectedGroupId" class="delete-icon" @click.stop="confirmDeleteGroup(g[GROUP_KEYS.id])" title="그룹 삭제">×</span>
                    </li>
                </ul>
            </aside>

            <section class="group-members">
                <div class="panel-header">
                    <div class="left">
                        <strong>그룹원</strong>
                        <p class="total">총 <span>{{ state.groupMemberList.length }}</span>명의 그룹원이 있습니다.</p>
                    </div>
                    <div class="right">
                        <div class="btn-area text-right">
                            <button class="btn-submit" @click="openInviteGroupModal" :disabled="!state.selectedGroupId">그룹원 초대</button>
                        </div>
                    </div>
                </div>

                <div class="table-wrapper">
                    <table class="table">
                        <thead>
                            <tr>
                                <th>이름</th>
                                <th>이메일</th>
                                <th style="width: 60px"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="!state.loading.groupMembers && state.groupMemberList.length === 0">
                                <td colspan="3" class="empty">그룹을 선택하거나 데이터가 없습니다.</td>
                            </tr>
                            <tr v-for="m in state.groupMemberList" :key="m[GROUP_MEMBER_KEYS.id]">
                                <td>{{ m[GROUP_MEMBER_KEYS.name] }}</td>
                                <td>{{ m[GROUP_MEMBER_KEYS.email] }}</td>
                                <td>
                                    <button class="btn-delete-small" @click="deleteGroupMember(m[GROUP_MEMBER_KEYS.cmidx])">삭제</button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <template v-if="state.groupList.length > 0">
                    <div class="divider"></div>

                    <div v-if="state.loading.groupLicense" class="empty">라이선스 정보를 불러오는 중...</div>
                    <div v-else-if="state.groupLicenseList && state.groupLicenseList.length > 0" class="license-management">
                        <div v-if="assignedGroupLicenses.length > 0" class="license-section">
                            <h4 class="section-title">할당된 라이선스</h4>
                            <div class="license-list">
                                <div v-for="license in assignedGroupLicenses" :key="license.productName" class="license-item assigned">
                                    <div class="license-header"><h5>{{ license.productName }}</h5></div>
                                    <div class="license-actions">
                                        <span v-if="license.expiredMemberCount > 0" 
                                              class="warning-icon" 
                                              :title="`라이선스 수량 부족으로 그룹멤버중 ${license.expiredMemberCount}명이 라이선스 접근이 제한됩니다.`"
                                              @click="showExpiredMemberWarning(license.expiredMemberCount)">⚠️</span>
                                        <button @click="unassignGroupLicense(license.productName)" class="btn-submit btn-danger">할당 해제</button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="unassignedGroupLicenses.length > 0" class="license-section">
                            <h4 class="section-title">미할당된 라이선스</h4>
                            <div class="license-list">
                                <div v-for="license in unassignedGroupLicenses" :key="license.productName" class="license-item unassigned">
                                    <div class="license-header"><h5>{{ license.productName }}</h5></div>
                                    <div class="license-actions">
                                        <span class="remaining-quantity">남은 수량: {{ license.remainingQuantity }}개</span>
                                        <button @click="assignGroupLicense(license.productName)" :disabled="license.remainingQuantity <= 0" class="btn-submit">할당</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="!state.selectedGroupId" class="empty">그룹을 선택해주세요.</div>
                </template>
            </section>
        </div>

        <!-- [모달] 그룹원 초대 -->
        <div v-if="state.modals.inviteGroup" class="ss-modal-backdrop" @click.self="closeInviteGroupModal">
            <div class="ss-modal modal-wide ss-modal--scroll-body">
                <h3>그룹원 초대</h3>
                <div class="ss-modal-scroll-body">
                <p class="muted">초대 가능 팀원 목록</p>
                <div class="invite-search-wrapper">
                    <div class="invite-search-box">
                        <input 
                            type="text" 
                            v-model.trim="state.inviteModalSearchKeyword" 
                            placeholder="이름 또는 이메일로 검색..." 
                            class="invite-search-input"
                        />
                        <span v-if="state.inviteModalSearchKeyword" class="invite-search-clear" @click="state.inviteModalSearchKeyword = ''">×</span>
                    </div>
                </div>
                <div class="invite-table-wrapper">
                    <div v-if="state.loading.invitable" class="empty">불러오는 중…</div>
                    <template v-else>
                        <div v-if="filteredInviteModalMembers.length === 0" class="empty">{{ state.inviteModalSearchKeyword ? '검색 결과가 없습니다.' : '초대 가능한 멤버가 없습니다.' }}</div>
                        <table v-else class="invite-table">
                            <thead>
                                <tr>
                                    <th style="width: 50px"></th>
                                    <th>이름</th>
                                    <th>이메일</th>
                                    <th style="width: 80px">상태</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="m in filteredInviteModalMembers" :key="m.id">
                                    <td><input type="checkbox" :checked="m.isMember" @change="onToggleGroupMember(m, $event)" /></td>
                                    <td>{{ m[GROUP_MEMBER_KEYS.name] || m.userName }}</td>
                                    <td>{{ m[GROUP_MEMBER_KEYS.email] || m.email }}</td>
                                    <td><span v-if="m.isMember" class="added-label">추가됨</span></td>
                                </tr>
                            </tbody>
                        </table>
                    </template>
                </div>
                </div>
                <div class="actions">
                    <button class="btn-submit" @click="closeInviteGroupModal">닫기</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'userGroupsTab',
    props: { selectedCrewId: { type: [Number, String], required: false } },
    data() {
        return {
            GROUP_KEYS: { id: 'id', name: 'groupName', crewId: 'crewId' },
            GROUP_MEMBER_KEYS: { id: 'id', groupId: 'groupId', cmidx: 'cmidx', name: 'userName', email: 'email' },
            state: {
                loading: { groups: false, groupMembers: false, invitable: false, groupLicense: false },
                groupList: [],
                selectedGroupId: null,
                groupMemberList: [],
                newGroupName: '',
                modals: { inviteGroup: false },
                inviteModalMembers: [],
                inviteModalSearchKeyword: '',
                groupLicenseList: [],
            },
        };
    },
    computed: {
        filteredInviteModalMembers() {
            if (!this.state.inviteModalSearchKeyword) {
                return this.state.inviteModalMembers;
            }
            const keyword = this.state.inviteModalSearchKeyword.toLowerCase();
            return this.state.inviteModalMembers.filter(member => {
                const name = ((member[this.GROUP_MEMBER_KEYS.name] || member.userName) || '').toLowerCase();
                const email = ((member[this.GROUP_MEMBER_KEYS.email] || member.email) || '').toLowerCase();
                return name.includes(keyword) || email.includes(keyword);
            });
        },
        assignedGroupLicenses() {
            if (!this.state.groupLicenseList) return [];
            return this.state.groupLicenseList.filter((l) => l.isAssignedToGroup);
        },
        unassignedGroupLicenses() {
            if (!this.state.groupLicenseList) return [];
            return this.state.groupLicenseList.filter((l) => !l.isAssignedToGroup);
        },
    },
    watch: {
        selectedCrewId: {
            immediate: true,
            async handler() {
                await this.refreshGroupTabData();
            },
        },
    },
    methods: {
        async refreshGroupTabData() {
            await this.fetchGroups();
            if (this.state.groupList.length > 0) {
                const first = this.state.groupList[0][this.GROUP_KEYS.id];
                this.selectGroup(first);
            } else {
                this.state.selectedGroupId = null;
                this.state.groupMemberList = [];
                this.state.groupLicenseList = [];
            }
        },
        fetchGroups() {
            if (!this.selectedCrewId) { this.state.groupList = []; return Promise.resolve(); }
            this.state.loading.groups = true;
            return new Promise((resolve) => {
                this.axiosCaller.get(
                    this,
                    this.APIs.LICENSE + '/getCrewGroupList',
                    { crewId: this.selectedCrewId },
                    (result) => {
                        try { this.state.groupList = this.getSafeArray(result); }
                        finally { this.state.loading.groups = false; resolve(); }
                    }
                );
            });
        },
        selectGroup(groupId) {
            this.state.selectedGroupId = groupId;
            this.fetchGroupMembers();
            this.fetchGroupLicenseInfo();
        },
        fetchGroupMembers() {
            if (!this.selectedCrewId || !this.state.selectedGroupId) return;
            this.state.loading.groupMembers = true;
            this.axiosCaller.get(
                this,
                this.APIs.LICENSE + '/getGroupMemberList',
                { crewId: this.selectedCrewId, groupId: this.state.selectedGroupId },
                (result) => {
                    try { this.state.groupMemberList = this.getSafeArray(result); }
                    finally { this.state.loading.groupMembers = false; }
                }
            );
        },
        createGroup() {
            if (!this.selectedCrewId || !this.state.newGroupName) return;
            const payload = { crewId: this.selectedCrewId, groupName: this.state.newGroupName };
            this.axiosCaller.post(this, this.APIs.LICENSE + '/setCrewGroup', payload, () => {
                this.state.newGroupName = '';
                this.refreshGroupTabData();
            });
        },
        openInviteGroupModal() {
            if (!this.selectedCrewId || !this.state.selectedGroupId) return;
            this.state.modals.inviteGroup = true;
            this.prepareInviteModalMembers();
        },
        closeInviteGroupModal() { 
            this.state.modals.inviteGroup = false; 
            this.state.inviteModalSearchKeyword = '';
        },
        async prepareInviteModalMembers() {
            this.state.loading.invitable = true;
            try {
                const invitableRes = await new Promise((resolve) => this.axiosCaller.get(
                    this,
                    this.APIs.LICENSE + '/getInviteGroupMemberList',
                    { crewId: this.selectedCrewId, groupId: this.state.selectedGroupId },
                    (r) => resolve(r)
                ));
                const invitable = this.getSafeArray(invitableRes).map(m => ({ ...m, isMember: false }));
                this.state.inviteModalMembers = invitable;
            } finally {
                this.state.loading.invitable = false;
            }
        },
        onToggleGroupMember(member, evt) {
            const checked = !!evt?.target?.checked;
            const original = member.isMember;
            member.isMember = checked;
            if (checked) {
                const payload = { crewId: this.selectedCrewId, groupId: this.state.selectedGroupId, memberId: String(member.id) };
                this.axiosCaller.post(this, this.APIs.LICENSE + '/inviteGroupMember', payload, (result) => {
                    const data = result?.data || {};
                    if (data.status === 'ok') this.fetchGroupMembers();
                    else member.isMember = original;
                });
            } else {
                const params = { crewId: this.selectedCrewId, groupId: this.state.selectedGroupId, memberId: String(member.id) };
                this.axiosCaller.delete(this, this.APIs.LICENSE + '/deleteGroupMember', params, (result) => {
                    const data = result?.data || {};
                    if (data.status === 'ok') this.fetchGroupMembers();
                    else member.isMember = original;
                });
            }
        },
        deleteGroupMember(memberId) {
            if (!this.selectedCrewId || !this.state.selectedGroupId || !memberId) return;
            if (!confirm('그룹원 삭제시 해당 그룹원에 할당된 라이선스가 해제됩니다. 진행하시겠습니까?')) return;
            const params = { crewId: this.selectedCrewId, groupId: this.state.selectedGroupId, memberId: String(memberId) };
            this.axiosCaller.delete(this, this.APIs.LICENSE + '/deleteGroupMember', params, () => this.fetchGroupMembers());
        },
        confirmDeleteGroup(groupId) {
            if (!groupId) return;
            if (!confirm('그룹을 삭제하시겠습니까?')) return;
            this.deleteGroup(groupId);
        },
        deleteGroup(groupId) {
            if (!this.selectedCrewId || !groupId) return;
            const params = { crewId: this.selectedCrewId, groupId };
            this.axiosCaller.delete(this, this.APIs.LICENSE + '/deleteCrewGroup', params, () => this.refreshGroupTabData());
        },
        fetchGroupLicenseInfo() {
            if (!this.selectedCrewId || !this.state.selectedGroupId) { this.state.groupLicenseList = []; return; }
            this.state.loading.groupLicense = true;
            this.axiosCaller.get(
                this,
                this.APIs.LICENSE + '/getGroupLicenseInfo',
                { crewId: this.selectedCrewId, groupId: this.state.selectedGroupId },
                (result) => {
                    try {
                        const data = result?.data || {};
                        if (data.status === 'ok') this.state.groupLicenseList = data.licenseList || [];
                        else this.state.groupLicenseList = [];
                    } finally { this.state.loading.groupLicense = false; }
                }
            );
        },
        assignGroupLicense(productName) {
            if (!this.selectedCrewId || !this.state.selectedGroupId) return;
            const payload = { crewId: this.selectedCrewId, groupId: this.state.selectedGroupId, productName };
            this.axiosCaller.post(this, this.APIs.LICENSE + '/assignGroupLicense', payload, (result) => {
                const data = result?.data || {};
                if (data.status === 'ok') {
                    this.gritterOk('라이선스가 할당되었습니다.');
                    this.fetchGroupLicenseInfo();
                } else {
                    this.gritterErr(data.msg || '라이선스 할당 실패');
                }
            });
        },
        unassignGroupLicense(productName) {
            if (!this.selectedCrewId || !this.state.selectedGroupId) return;
            const payload = { crewId: this.selectedCrewId, groupId: this.state.selectedGroupId, productName };
            this.axiosCaller.post(this, this.APIs.LICENSE + '/unassignGroupLicense', payload, (result) => {
                const data = result?.data || {};
                if (data.status === 'ok') {
                    this.gritterOk('라이선스 할당이 해제되었습니다.');
                    this.fetchGroupLicenseInfo();
                } else {
                    this.gritterErr(data.msg || '라이선스 할당 해제 실패');
                }
            });
        },
        showExpiredMemberWarning(expiredMemberCount) {
            alert(`라이선스 수량 부족으로 그룹멤버중 ${expiredMemberCount}명이 라이선스 접근이 제한됩니다.`);
        },
        // 공통 유틸
        getData(resOrData) { return resOrData?.data ?? resOrData; },
        getSafeArray(resOrData) {
            const result = this.getData(resOrData);
            if (result?.status && result.status !== 'ok') return [];
            const topCandidates = [result?.list, result?.rows, result?.content, result?.data];
            for (const v of topCandidates) if (Array.isArray(v)) return v;
            const nestedCandidates = [result?.data?.list, result?.data?.rows, result?.data?.content];
            for (const v of nestedCandidates) if (Array.isArray(v)) return v;
            if (result && typeof result === 'object') {
                for (const k of Object.keys(result)) if (Array.isArray(result[k])) return result[k];
            }
            return [];
        },
        gritterOk(msg) {
            this.swalUtils?.gritter
                ? this.swalUtils.gritter('Success', msg || '성공', 'success', 2000)
                : console.log('[OK]', msg);
        },
        gritterErr(msg) {
            const m = msg || '정상적인 접근이 아닙니다. 다시 시도해주세요.';
            this.swalUtils?.gritter
                ? this.swalUtils.gritter('error', m, 'error', 2000)
                : console.error('[ERR]', m);
        },
    },
};
</script>

<style scoped>
.license-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.warning-icon {
    cursor: pointer;
    font-size: 18px;
    color: #ff6b6b;
    display: inline-flex;
    align-items: center;
    transition: transform 0.2s;
}

.warning-icon:hover {
    transform: scale(1.2);
}

.invite-search-wrapper {
    margin-bottom: 16px;
}

.invite-search-box {
    position: relative;
    max-width: 100%;
}

.invite-search-input {
    width: 100%;
    padding: 8px 32px 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    box-sizing: border-box;
}

.invite-search-input:focus {
    outline: none;
    border-color: #007bff;
}

.invite-search-clear {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    font-size: 20px;
    color: #999;
    line-height: 1;
    padding: 0 4px;
}

.invite-search-clear:hover {
    color: #333;
}
</style>


